import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '@tabler/icons-react';
import { getDropdownPosition } from './dropdownPosition';
import { useDropdownDismiss } from './useDropdownDismiss';
import './dropdown.css';

/**
 * Type C — search-ahead. A free-text input that surfaces two-line (code +
 * description) results as you type. The parent owns the query (`value`),
 * filtering (`results`), and commit (`onSelect`); free-text is the input value
 * itself. Keyboard nav (↑/↓ to move, Enter to pick, Esc to close) is kept with
 * no visible hint.
 */
export interface SearchAheadResult {
  code: string;
  description: string;
}

interface SearchAheadProps<T extends SearchAheadResult> {
  value: string;
  results: T[];
  onSearch: (text: string) => void;
  onSelect: (result: T) => void;
  /** Fires with the current text on blur — lets the host commit free-text. */
  onBlur?: (value: string) => void;
  /** Small caption shown beneath the field (e.g. the selected code), hidden while searching. */
  secondaryText?: string;
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
}

export function SearchAhead<T extends SearchAheadResult>({
  value,
  results,
  onSearch,
  onSelect,
  onBlur,
  secondaryText,
  label = 'Product / Service',
  placeholder = 'Type or select a product code or description',
  autoFocus,
}: SearchAheadProps<T>) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const close = React.useCallback(() => setOpen(false), []);
  useDropdownDismiss(dropdownRef, triggerRef, close, open);

  // Clamp + keep the highlighted row in view.
  useEffect(() => {
    setHighlighted((h) => Math.min(h, Math.max(0, results.length - 1)));
  }, [results.length]);

  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.children[highlighted] as HTMLElement | undefined;
    row?.scrollIntoView({ block: 'nearest' });
  }, [highlighted, open]);

  const pick = (result: T) => {
    onSelect(result);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlighted((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      const choice = results[highlighted];
      if (open && choice) {
        e.preventDefault();
        pick(choice);
      }
    }
  };

  const showDropdown = open && results.length > 0;

  return (
    <div className="sm4rt-search-ahead" ref={triggerRef}>
      <div className={`sm4rt-select__trigger${showDropdown ? ' sm4rt-select__trigger--open' : ''}`}>
        <span className="sm4rt-select__trigger-label">{label}</span>
        <input
          className="sm4rt-select__value"
          value={value}
          placeholder={placeholder}
          spellCheck={false}
          autoFocus={autoFocus}
          onChange={(e) => {
            onSearch(e.target.value);
            setOpen(true);
            setHighlighted(0);
          }}
          onFocus={() => value && setOpen(true)}
          onBlur={() => onBlur?.(value)}
          onKeyDown={handleKeyDown}
        />
        {value && (
          <button
            type="button"
            className="sm4rt-select__clear"
            aria-label="Clear"
            // Keep input focus so blur-commit doesn't fire with the stale value.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onSearch('');
              setOpen(false);
            }}
          >
            <IconX size={11} />
          </button>
        )}
      </div>

      {secondaryText && !open && (
        <div className="sm4rt-search-ahead__sub">{secondaryText}</div>
      )}

      {showDropdown &&
        triggerRef.current &&
        createPortal(
          <div
            ref={dropdownRef}
            className="sm4rt-select__dropdown"
            style={getDropdownPosition(triggerRef.current, { gap: 0 })}
          >
            <div className="sm4rt-dd__list sm4rt-dd__list--scroll" ref={listRef}>
              {results.map((result, i) => (
                <div
                  key={result.code}
                  className={`sm4rt-dd__item sm4rt-dd__item--two-line ${
                    i === highlighted ? 'sm4rt-dd__item--active' : ''
                  }`}
                  onMouseEnter={() => setHighlighted(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(result);
                  }}
                >
                  {/* Inverted: name/description bold on top, short code beneath.
                      The size classes are unchanged (top = bold 13px, bottom =
                      muted 11px); only the content order/emphasis is swapped. */}
                  <div className="sm4rt-dd__item-code">{result.description || result.code}</div>
                  {result.description && (
                    <div className="sm4rt-dd__item-desc">{result.code}</div>
                  )}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
